import { useEffect, useState, useCallback } from "react";
import { Search, Download, Trash2, Eye, X } from "lucide-react";
import api from "@/lib/api";

const cols = ["name", "email", "organisation", "enquiry_type", "phone", "created_at", "status"];

function toCSV(rows) {
  if (!rows.length) return "";
  const keys = Object.keys(rows[0]);
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  return [keys.join(","), ...rows.map((r) => keys.map((k) => esc(r[k])).join(","))].join("\n");
}

export default function EnquiriesTable() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [view, setView] = useState(null);

  const load = useCallback(() => { api.get("/admin/enquiries").then((r) => setRows(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter((r) => JSON.stringify(r).toLowerCase().includes(q.toLowerCase()) && (filter === "all" || r.status === filter));

  const setStatus = async (id, status) => { await api.patch(`/admin/enquiries/${id}`, { status }); load(); };
  const del = async (id) => { if (window.confirm("Delete this enquiry?")) { await api.delete(`/admin/enquiries/${id}`); load(); } };
  const download = () => {
    const blob = new Blob([toCSV(filtered)], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "enquiries.csv"; a.click();
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-6"><h1 className="font-heading font-700 text-2xl text-lp-blue">Enquiries</h1><span className="text-sm text-lp-muted bg-white border border-lp-border rounded-sm px-2.5 py-0.5">{rows.length}</span></div>
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-lp-muted" />
          <input data-testid="admin-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="w-full border border-lp-border rounded-sm pl-9 pr-4 py-2.5 text-sm bg-white focus:outline-none focus:border-lp-cyan" />
        </div>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="border border-lp-border rounded-sm px-3 py-2.5 text-sm bg-white"><option value="all">All Status</option><option value="new">New</option><option value="reviewed">Reviewed</option><option value="closed">Closed</option></select>
        <button onClick={download} data-testid="admin-export" className="inline-flex items-center gap-2 border border-lp-blue text-lp-blue font-600 px-4 py-2.5 rounded-sm text-sm hover:bg-lp-blue hover:text-white transition-colors"><Download size={15} /> Export CSV</button>
      </div>

      <div className="bg-white border border-lp-border rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-lp-grey text-left"><tr>{cols.map((c) => <th key={c} className="px-4 py-3 font-600 text-lp-ink capitalize whitespace-nowrap">{c.replace("_", " ")}</th>)}<th className="px-4 py-3">Actions</th></tr></thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} className="border-t border-lp-border hover:bg-lp-grey/50">
                {cols.map((c) => (
                  <td key={c} className="px-4 py-3 text-lp-muted whitespace-nowrap max-w-[200px] truncate">
                    {c === "status" ? (
                      <select value={r.status} onChange={(e) => setStatus(r.id, e.target.value)} className="border border-lp-border rounded-sm px-2 py-1 text-xs"><option value="new">New</option><option value="reviewed">Reviewed</option><option value="closed">Closed</option></select>
                    ) : c === "created_at" ? new Date(r[c]).toLocaleDateString("en-IN") : r[c] || "-"}
                  </td>
                ))}
                <td className="px-4 py-3 flex gap-2"><button onClick={() => setView(r)} className="text-lp-blue"><Eye size={17} /></button><button onClick={() => del(r.id)} className="text-destructive"><Trash2 size={17} /></button></td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={cols.length + 1} className="px-4 py-10 text-center text-lp-muted">No enquiries found.</td></tr>}
          </tbody>
        </table>
      </div>

      {view && (
        <div className="fixed inset-0 z-50 bg-lp-ink/50 flex items-center justify-center p-4" onClick={() => setView(null)}>
          <div className="bg-white rounded-sm max-w-lg w-full p-8 relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setView(null)} className="absolute top-4 right-4 text-lp-muted"><X size={20} /></button>
            <h3 className="font-heading font-700 text-xl text-lp-blue mb-4">Enquiry Details</h3>
            <div className="space-y-2 text-sm max-h-[60vh] overflow-y-auto">
              {Object.entries(view).filter(([k]) => k !== "id").map(([k, v]) => (
                <div key={k} className="flex gap-3"><span className="font-600 text-lp-ink w-32 capitalize shrink-0">{k.replace("_", " ")}</span><span className="text-lp-muted break-words">{String(v)}</span></div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
